import api from './api';

export type CategoriaSolicitacao =
  | 'ALTERAR_VALOR'
  | 'ALTERAR_OBJETO'
  | 'ALTERAR_BENEFICIARIO'
  | 'CANCELAR'
  | 'DUVIDA'
  | 'OUTRO';

/** `label` aparece nas listas; `botao` é o texto do botão grande no formulário. */
export const CATEGORIAS_SOLICITACAO: Array<{ value: CategoriaSolicitacao; label: string; botao: string; legada?: boolean }> = [
  { value: 'ALTERAR_VALOR', label: 'Mudar o valor', botao: 'Mudar o valor' },
  { value: 'ALTERAR_OBJETO', label: 'Mudar o objeto', botao: 'Mudar o objeto' },
  { value: 'ALTERAR_BENEFICIARIO', label: 'Trocar a instituição', botao: 'Trocar a instituição', legada: true },
  { value: 'CANCELAR', label: 'Cancelar', botao: 'Cancelar' },
  { value: 'DUVIDA', label: 'Dúvida', botao: 'Tirar uma dúvida' },
  { value: 'OUTRO', label: 'Outro', botao: 'Outro assunto' },
];

/** Rótulo da categoria; em "Outro" inclui o tipo digitado ("Outro: trocar a instituição"). */
export const rotuloCategoria = (valor: string, outro?: string | null) => {
  const base = CATEGORIAS_SOLICITACAO.find((c) => c.value === valor)?.label ?? valor;
  return valor === 'OUTRO' && outro ? `${base}: ${outro}` : base;
};

/** Categorias oferecidas em pedidos novos; as legadas só existem para rotular pedidos antigos. */
export const CATEGORIAS_NOVAS = CATEGORIAS_SOLICITACAO.filter((c) => !c.legada);

export const CATEGORIA_OUTRO_MAXIMO = 100;

/** Um tipo digitado em "Outro" e quantas vezes apareceu. */
export type CategoriaOutro = { texto: string; quantidade: number; ultimaEm: string };

/** "Pedido nº 12" (ou o protocolo, para pedidos antigos sem número). */
export const rotuloPedido = (s: { numero?: number | null; protocolo: string }) =>
  s.numero != null ? `Pedido nº ${s.numero}` : `Pedido ${s.protocolo}`;

/** Última mensagem da equipe (resposta, resolvido ou motivo da recusa), para mostrar sem abrir o pedido. */
export const ultimaMensagemDaEquipe = (s: Solicitacao): string | null => {
  for (let i = s.historico.length - 1; i >= 0; i--) {
    const h = s.historico[i];
    if (['RESPOSTA', 'ATENDIDA', 'RECUSADA'].includes(h.acao) && h.observacao) return h.observacao;
  }
  return null;
};

/** A que a solicitação se refere: emenda, pré-projeto ou, sem alvo, a tela de origem. */
export const rotuloAlvo = (s: Pick<Solicitacao, 'emendaCodigo' | 'preProjetoCodigo' | 'tituloTela'>) =>
  s.emendaCodigo
    ? `Emenda ${s.emendaCodigo}`
    : s.preProjetoCodigo
      ? `Pré-projeto ${s.preProjetoCodigo}`
      : s.tituloTela
        ? `Tela: ${s.tituloTela}`
        : 'Geral';

export const STATUS_SOLICITACAO_ORDEM = ['ABERTA', 'EM_ANALISE', 'ATENDIDA', 'RECUSADA'] as const;

export const STATUS_SOLICITACAO_LABEL: Record<string, string> = {
  ABERTA: 'Enviado',
  EM_ANALISE: 'Em análise',
  ATENDIDA: 'Resolvido',
  RECUSADA: 'Não aceito',
};

/** Explicação em uma frase, para quem acompanha o pedido. */
export const STATUS_SOLICITACAO_EXPLICACAO: Record<string, string> = {
  ABERTA: 'Seu pedido foi enviado e a equipe vai olhar em breve.',
  EM_ANALISE: 'A equipe está cuidando do seu pedido.',
  ATENDIDA: 'Pronto: a equipe resolveu o seu pedido.',
  RECUSADA: 'A equipe não pôde aceitar este pedido. O motivo está abaixo.',
};

export const ANEXO_TAMANHO_MAXIMO_BYTES = 5 * 1024 * 1024;
export const ANEXO_EXTENSOES = ['pdf', 'png', 'jpg', 'jpeg', 'doc', 'docx', 'xls', 'xlsx'];

export type EmendaElegivel = {
  id: string;
  codigo: string;
  objeto?: string | null;
  tipoTransferencia: string;
  aceitaPlano: boolean;
  planos: Array<{ id: string; titulo?: string | null; status?: string | null }>;
};

export type SolicitacaoAnexo = {
  id: string;
  nomeOriginal: string;
  mimeType?: string | null;
  tamanhoBytes: number;
  criadoEm: string;
};

export type SolicitacaoEvento = {
  acao: string;
  statusAnterior?: string | null;
  statusNovo?: string | null;
  observacao?: string | null;
  usuario: string;
  criadaEm: string;
};

export type Solicitacao = {
  id: string;
  protocolo: string;
  /** Número curto do pedido ("Pedido nº 12"). */
  numero?: number | null;
  emendaId?: string | null;
  emendaCodigo?: string | null;
  planoTrabalhoId?: string | null;
  preProjetoId?: string | null;
  preProjetoCodigo?: string | null;
  /** Rota interna da tela de origem (informativa). */
  rota?: string | null;
  tituloTela?: string | null;
  categoria: CategoriaSolicitacao;
  /** Só em OUTRO: o tipo de alteração, como o parlamentar digitou. */
  categoriaOutro?: string | null;
  descricao: string;
  status: string;
  solicitanteNome: string;
  responsavelNome?: string | null;
  criadaEm: string;
  atualizadaEm: string;
  anexos: SolicitacaoAnexo[];
  historico: SolicitacaoEvento[];
};

/** Linha da fila da equipe. */
export type SolicitacaoResumo = {
  id: string;
  protocolo: string;
  numero?: number | null;
  status: string;
  categoria: CategoriaSolicitacao;
  categoriaOutro?: string | null;
  solicitanteNome: string;
  responsavelNome?: string | null;
  emendaCodigo?: string | null;
  preProjetoCodigo?: string | null;
  tituloTela?: string | null;
  criadaEm: string;
  atualizadaEm: string;
};

export type SolicitacaoFila = {
  itens: SolicitacaoResumo[];
  pagina: number;
  tamanho: number;
  total: number;
  contagemPorStatus: Record<string, number>;
};

export type FiltroFila = { status?: string; solicitante?: string; emenda?: string; pagina?: number; tamanho?: number };

export type NovaSolicitacao = {
  emendaId?: string | null;
  planoTrabalhoId?: string | null;
  preProjetoId?: string | null;
  categoria: CategoriaSolicitacao;
  categoriaOutro?: string | null;
  descricao: string;
  rota?: string | null;
  tituloTela?: string | null;
};

/** Mensagem do backend (`{ error }`), com fallback para o texto padrão. */
export function mensagemDeErro(err: unknown, padrao: string): string {
  const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
  return data?.error || padrao;
}

export const solicitacaoService = {
  emendasElegiveis: async (): Promise<EmendaElegivel[]> =>
    (await api.get<EmendaElegivel[]>('/solicitacoes/emendas-elegiveis')).data,
  minhas: async (): Promise<Solicitacao[]> => (await api.get<Solicitacao[]>('/solicitacoes/minhas')).data,
  obter: async (id: string): Promise<Solicitacao> => (await api.get<Solicitacao>(`/solicitacoes/${id}`)).data,
  criar: async (dados: NovaSolicitacao): Promise<Solicitacao> =>
    (await api.post<Solicitacao>('/solicitacoes', dados)).data,
  fila: async (filtro: FiltroFila): Promise<SolicitacaoFila> => {
    const params: Record<string, string | number> = {};
    if (filtro.status) params.status = filtro.status;
    if (filtro.solicitante?.trim()) params.solicitante = filtro.solicitante.trim();
    if (filtro.emenda?.trim()) params.emenda = filtro.emenda.trim();
    params.pagina = filtro.pagina ?? 0;
    params.tamanho = filtro.tamanho ?? 20;
    return (await api.get<SolicitacaoFila>('/solicitacoes', { params })).data;
  },
  outros: async (): Promise<CategoriaOutro[]> => (await api.get<CategoriaOutro[]>('/solicitacoes/outros')).data,
  assumir: async (id: string): Promise<Solicitacao> => (await api.post<Solicitacao>(`/solicitacoes/${id}/assumir`)).data,
  responder: async (id: string, mensagem: string): Promise<Solicitacao> =>
    (await api.post<Solicitacao>(`/solicitacoes/${id}/responder`, { mensagem })).data,
  atender: async (id: string, mensagem?: string): Promise<Solicitacao> =>
    (await api.post<Solicitacao>(`/solicitacoes/${id}/atender`, { mensagem: mensagem ?? '' })).data,
  recusar: async (id: string, motivo: string): Promise<Solicitacao> =>
    (await api.post<Solicitacao>(`/solicitacoes/${id}/recusar`, { motivo })).data,
  anexar: async (id: string, arquivo: File): Promise<Solicitacao> => {
    const form = new FormData();
    form.append('file', arquivo);
    return (
      await api.post<Solicitacao>(`/solicitacoes/${id}/anexos`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data;
  },
};
